import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingState, EmptyState } from '../../components/ui/Spinner';
import { formatAmount, calculateDisplayRate, formatDisplayRate } from '../../lib/format';
import { api, DailyActivityReport as DailyActivityReportType } from '../../lib/api';

const CRYPTO_STABLECOINS = new Set(['USDT', 'USDC']);

interface DailyActivityReportProps {
  entityId: string;
}

export default function DailyActivityReport({ entityId }: DailyActivityReportProps) {
  const { t } = useTranslation('reports');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const today = new Date().toISOString().split('T')[0];
  const selectedDate = searchParams.get('date') || today;
  // dateTo defaults to selectedDate (single-day mode). When user picks a different
  // dateTo, we send both to the backend and aggregate over the full inclusive range.
  const selectedDateTo = searchParams.get('dateTo') || selectedDate;
  const selectedCashDeskId = searchParams.get('cashDeskId') || '';
  const isRange = selectedDate !== selectedDateTo;

  const setSelectedDate = useCallback((date: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (date === today) next.delete('date'); else next.set('date', date);
      // If new start > current end, drop dateTo so the range stays valid.
      const currentEnd = next.get('dateTo');
      if (currentEnd && currentEnd < date) next.delete('dateTo');
      return next;
    }, { replace: true });
  }, [setSearchParams, today]);

  const setSelectedDateTo = useCallback((date: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const start = next.get('date') || today;
      // Empty or equal to start = single-day mode (no dateTo param).
      if (!date || date === start) next.delete('dateTo');
      else next.set('dateTo', date);
      return next;
    }, { replace: true });
  }, [setSearchParams, today]);

  const setSelectedCashDeskId = useCallback((id: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (id) next.set('cashDeskId', id); else next.delete('cashDeskId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const { data: cashDesks } = useQuery({
    queryKey: ['cash-desks', entityId],
    queryFn: () => api.cashDesks.list(entityId).then(res => res.data),
    enabled: !!entityId && isAdmin,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['report-daily-activity', entityId, selectedDate, selectedDateTo, selectedCashDeskId],
    queryFn: () => api.reports.dailyActivity(
      entityId,
      selectedDate,
      isRange ? selectedDateTo : undefined,
      selectedCashDeskId || undefined,
    ),
    enabled: !!entityId,
  });

  // Split buy/sell transactions into EUR-pair and other
  const { eurBuy, eurSell, otherBuy, otherSell } = useMemo(() => {
    if (!data) return { eurBuy: [], eurSell: [], otherBuy: [], otherSell: [] };

    const isEurPairBuy = (tx: { currencyIn: string; currencyOut: string }) =>
      tx.currencyIn === 'EUR' && CRYPTO_STABLECOINS.has(tx.currencyOut);
    const isEurPairSell = (tx: { currencyIn: string; currencyOut: string }) =>
      CRYPTO_STABLECOINS.has(tx.currencyIn) && tx.currencyOut === 'EUR';

    return {
      eurBuy: data.transactions.buy.filter(isEurPairBuy),
      otherBuy: data.transactions.buy.filter(tx => !isEurPairBuy(tx)),
      eurSell: data.transactions.sell.filter(isEurPairSell),
      otherSell: data.transactions.sell.filter(tx => !isEurPairSell(tx)),
    };
  }, [data]);

  // Split expenses: salary vs non-salary (detected by group_type)
  const { salaryExpenses, nonSalaryExpenses } = useMemo(() => {
    if (!data) return { salaryExpenses: [], nonSalaryExpenses: [] };
    return {
      salaryExpenses: data.transactions.expense.filter(e => e.groupType === 'salary'),
      nonSalaryExpenses: data.transactions.expense.filter(e => e.groupType !== 'salary'),
    };
  }, [data]);

  if (isLoading) return <LoadingState message={t('dailyActivity.loading')} />;
  if (error) return <EmptyState title={t('dailyActivity.error.title')} description={t('dailyActivity.error.description')} />;
  if (!data) return <EmptyState title={t('dailyActivity.empty.title')} description={t('dailyActivity.empty.description')} />;

  const hasOtherExchanges = otherBuy.length > 0 || otherSell.length > 0;
  const eurPurchaseRate = data.summary.eurPurchaseRate ? parseFloat(data.summary.eurPurchaseRate) : null;

  return (
    <div className="daily-activity-report">
      <div className="card mb-4">
        <div className="date-picker-row">
          <label>{t('dailyActivity.dateFrom')}:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="form-input date-input"
          />
          <label>{t('dailyActivity.dateTo')}:</label>
          <input
            type="date"
            value={selectedDateTo}
            min={selectedDate}
            onChange={(e) => setSelectedDateTo(e.target.value)}
            className="form-input date-input"
          />
          {isRange && (
            <span className="range-badge">{t('dailyActivity.rangeBadge')}</span>
          )}
          {isAdmin && cashDesks && cashDesks.length > 0 && (
            <>
              <label>{t('dailyActivity.cashDesk')}:</label>
              <select
                value={selectedCashDeskId}
                onChange={(e) => setSelectedCashDeskId(e.target.value)}
                className="form-input cash-desk-select"
              >
                <option value="">{t('dailyActivity.allCashDesks')}</option>
                {cashDesks.map((desk) => (
                  <option key={desk.id} value={desk.id}>{desk.name}</option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      {user?.role !== 'cashier' ? (
        <div className="daily-activity-grid">
          <div className="summary-column">
            <DailyActivitySummary summary={data.summary} isRange={isRange} />
          </div>
          <div className="wallets-column">
            <DailyActivityWallets wallets={data.wallets} cashDeskName={data.cashDeskName} />
          </div>
        </div>
      ) : (
        <DailyActivityWallets wallets={data.wallets} cashDeskName={data.cashDeskName} />
      )}

      {/* EUR ↔ USDT/USDC exchanges */}
      <div className="transactions-grid">
        <ExchangeTransactionTable
          title={t('dailyActivity.tables.buy')}
          subtitle="EUR → USDT / USDC"
          transactions={eurBuy}
          colorScheme="buy"
        />
        <ExchangeTransactionTable
          title={t('dailyActivity.tables.sell')}
          subtitle="USDT / USDC → EUR"
          transactions={eurSell}
          colorScheme="sell"
        />
      </div>

      {/* Other exchanges (non-EUR pairs) */}
      {hasOtherExchanges && (
        <div className="transactions-grid">
          <ExchangeTransactionTable
            title={t('dailyActivity.tables.buyOther')}
            transactions={otherBuy}
            colorScheme="buy"
          />
          <ExchangeTransactionTable
            title={t('dailyActivity.tables.sellOther')}
            transactions={otherSell}
            colorScheme="sell"
          />
        </div>
      )}

      {/* Expenses and debts */}
      <div className="transactions-grid">
        <ExpenseTransactionTable
          title={t('dailyActivity.tables.expense')}
          transactions={nonSalaryExpenses}
          eurPurchaseRate={eurPurchaseRate}
          eurPurchaseRateSource={data.summary.eurPurchaseRateSource}
          eurPurchaseRateDate={data.summary.eurPurchaseRateDate}
          isRange={isRange}
        />
        <LegacyTransactionTable
          title={t('dailyActivity.tables.debt')}
          transactions={data.transactions.debt}
          colorScheme="debt"
          nameField="clientName"
        />
      </div>

      {/* Salaries */}
      <div className="transactions-grid">
        <SalaryExpenseTable
          title={t('dailyActivity.tables.salaries')}
          transactions={salaryExpenses}
          eurPurchaseRate={eurPurchaseRate}
          eurPurchaseRateSource={data.summary.eurPurchaseRateSource}
          eurPurchaseRateDate={data.summary.eurPurchaseRateDate}
          isRange={isRange}
        />
        <div />
      </div>

      <style>{`
        .daily-activity-report {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .date-picker-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .date-picker-row label {
          font-weight: 500;
          color: var(--color-text-muted);
        }
        .date-input {
          width: auto;
          min-width: 180px;
        }
        .cash-desk-select {
          width: auto;
          min-width: 180px;
        }
        .range-badge {
          font-size: 11px;
          padding: 3px 8px;
          background: var(--color-surface-hover);
          color: var(--color-primary);
          border-radius: var(--radius);
          font-weight: 500;
          border: 1px solid var(--color-border);
        }
        .daily-activity-grid {
          display: grid;
          grid-template-columns: 320px 1fr;
          gap: 16px;
        }
        .transactions-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        @media (max-width: 1200px) {
          .daily-activity-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 900px) {
          .transactions-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

interface SummaryProps {
  summary: DailyActivityReportType['summary'];
  isRange: boolean;
}

function DailyActivitySummary({ summary, isRange }: SummaryProps) {
  const { t } = useTranslation('reports');

  const formatValue = (value: string, currency: string, label?: string) => {
    const num = parseFloat(value) || 0;
    return `${formatAmount(num.toString(), currency)} ${label || currency}`;
  };

  const profitEur = parseFloat(summary.profit.eur) || 0;
  const netProfitEur = summary.profitLoss.eur !== null ? parseFloat(summary.profitLoss.eur) : null;
  const hasEurPurchaseRate = summary.eurPurchaseRate !== null;

  return (
    <div className="card summary-card">
      <h3 className="card-title">{t('dailyActivity.summary.title')}</h3>
      <div className="info-hint">{t('dailyActivity.summary.pairsHint')}</div>

      <div className="summary-item">
        <span className="summary-label">{t('dailyActivity.summary.totalBuy')}</span>
        <span className="summary-value">
          <span>{formatValue(summary.totalBuy.eur, 'EUR')}</span>
          <span className="secondary">{formatValue(summary.totalBuy.usdt, 'USDT', 'USDT / USDC')}</span>
        </span>
      </div>

      <div className="summary-item">
        <span className="summary-label">{t('dailyActivity.summary.totalSell')}</span>
        <span className="summary-value">
          <span>{formatValue(summary.totalSell.eur, 'EUR')}</span>
          <span className="secondary">{formatValue(summary.totalSell.usdt, 'USDT', 'USDT / USDC')}</span>
        </span>
      </div>

      <div className="summary-item">
        <span className="summary-label">{t('dailyActivity.summary.totalExpenses')}</span>
        <span className="summary-value">
          <span>{formatValue(summary.totalExpenses.eur, 'EUR')}</span>
          <span className="secondary">{formatValue(summary.totalExpenses.usdt, 'USDT', 'USDT / USDC')}</span>
        </span>
      </div>

      <div className="summary-item">
        <span className="summary-label">{t('dailyActivity.summary.totalDebt')}</span>
        <span className="summary-value">
          <span>{formatValue(summary.totalDebt.eur, 'EUR')}</span>
          <span className="secondary">{formatValue(summary.totalDebt.usdt, 'USDT', 'USDT / USDC')}</span>
        </span>
      </div>

      <div className="summary-divider" />

      <div className="summary-item highlight">
        <span className="summary-label">
          {t('dailyActivity.summary.profit')}
          <div className="info-hint-small">{t('dailyActivity.summary.profitHint')}</div>
        </span>
        <span className={`summary-value ${profitEur >= 0 ? 'positive' : 'negative'}`}>
          <span>{formatValue(summary.profit.eur, 'EUR')}</span>
        </span>
      </div>

      <div className="summary-item">
        <span className="summary-label">
          {t('dailyActivity.summary.profitLoss')}
          <div className="info-hint-small">{t('dailyActivity.summary.profitLossHint')}</div>
        </span>
        {netProfitEur !== null ? (
          <span className={`summary-value ${netProfitEur >= 0 ? 'positive' : 'negative'}`}>
            <span>{formatValue(summary.profitLoss.eur!, 'EUR')}</span>
          </span>
        ) : (
          <span className="summary-value warning-text">
            <span>—</span>
            <span className="secondary">{t('dailyActivity.summary.noEurPurchaseRate')}</span>
          </span>
        )}
      </div>

      <div className="summary-item">
        <span className="summary-label">{t('dailyActivity.summary.profitMargin')}</span>
        <span className={`summary-value ${parseFloat(summary.profitMargin) >= 0 ? 'positive' : 'negative'}`}>
          {summary.profitMargin}%
        </span>
      </div>

      {hasEurPurchaseRate && (
        <>
          <div className="summary-divider" />
          <div className="summary-item">
            <span className="summary-label">
              {t('dailyActivity.summary.eurPurchaseRate')}
              <div className="info-hint-small">{t('dailyActivity.summary.eurPurchaseRateHint')}</div>
              {isRange && (
                <div className="info-hint-small" style={{ fontStyle: 'italic' }}>
                  {t('dailyActivity.summary.eurPurchaseRateWeightedAvg')}
                </div>
              )}
            </span>
            <span className="summary-value">
              <span>{parseFloat(summary.eurPurchaseRate!).toFixed(5)}</span>
            </span>
          </div>
          {summary.eurPurchaseRateSource === 'previous' && summary.eurPurchaseRateDate && (
            <div className="prev-day-rate-hint">
              {t('dailyActivity.summary.eurPurchaseRatePrevDay', { date: summary.eurPurchaseRateDate })}
            </div>
          )}
        </>
      )}

      <style>{`
        .summary-card {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .info-hint {
          font-size: 11px;
          color: var(--color-text-muted);
          background: var(--color-surface-hover);
          padding: 6px 10px;
          border-radius: var(--radius);
          border-left: 3px solid var(--color-primary);
          line-height: 1.4;
          margin-top: -4px;
        }
        .info-hint-small {
          font-size: 10px;
          color: var(--color-text-muted);
          font-weight: 400;
          line-height: 1.3;
          margin-top: 2px;
        }
        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 8px 0;
        }
        .summary-item.highlight {
          background: var(--color-surface-hover);
          padding: 12px;
          margin: 0 -16px;
          border-radius: var(--radius);
        }
        .summary-label {
          font-weight: 500;
          color: var(--color-text-muted);
          font-size: 13px;
        }
        .summary-value {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          font-family: monospace;
          font-weight: 600;
          gap: 2px;
        }
        .summary-value .secondary {
          font-size: 12px;
          color: var(--color-text-muted);
          font-weight: 400;
        }
        .summary-value.positive {
          color: var(--color-success);
        }
        .summary-value.negative {
          color: var(--color-error);
        }
        .warning-text {
          color: var(--color-warning) !important;
        }
        .summary-divider {
          height: 1px;
          background: var(--color-border);
          margin: 8px 0;
        }
        .prev-day-rate-hint {
          font-size: 11px;
          color: var(--color-warning);
          background: color-mix(in srgb, var(--color-warning) 10%, transparent);
          padding: 4px 8px;
          border-radius: var(--radius);
          border-left: 3px solid var(--color-warning);
          line-height: 1.4;
        }
      `}</style>
    </div>
  );
}

interface WalletsProps {
  wallets: DailyActivityReportType['wallets'];
  cashDeskName: string | null;
}

function DailyActivityWallets({ wallets, cashDeskName }: WalletsProps) {
  const { t } = useTranslation('reports');

  if (!wallets || wallets.length === 0) {
    return (
      <div className="card">
        <EmptyState
          title={t('dailyActivity.wallets.empty.title')}
          description={t('dailyActivity.wallets.empty.description')}
        />
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="card-title">{t('dailyActivity.wallets.title')}</h3>
      {cashDeskName && (
        <div className="info-hint" style={{ marginBottom: 8 }}>
          {t('dailyActivity.wallets.groupHint', { groupName: cashDeskName })}
        </div>
      )}
      <div className="table-scroll">
        <table className="table table-compact">
          <thead>
            <tr>
              <th>{t('dailyActivity.wallets.wallet')}</th>
              <th>{t('dailyActivity.wallets.currency')}</th>
              <th className="text-right">{t('dailyActivity.wallets.opening')}</th>
              <th className="text-right">{t('dailyActivity.wallets.closing')}</th>
              <th className="text-right">{t('dailyActivity.wallets.difference')}</th>
            </tr>
          </thead>
          <tbody>
            {wallets.map((wallet) => {
              const diff = parseFloat(wallet.difference) || 0;
              return (
                <tr key={wallet.walletId}>
                  <td>{wallet.walletName}</td>
                  <td>{wallet.currency}</td>
                  <td className="text-right font-mono">{formatAmount(wallet.openingBalance)}</td>
                  <td className="text-right font-mono">{formatAmount(wallet.closingBalance)}</td>
                  <td className={`text-right font-mono ${diff > 0 ? 'text-success' : diff < 0 ? 'text-error' : ''}`}>
                    {diff > 0 ? '+' : ''}{formatAmount(wallet.difference)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <style>{`
        .info-hint {
          font-size: 11px;
          color: var(--color-text-muted);
          background: var(--color-surface-hover);
          padding: 6px 10px;
          border-radius: var(--radius);
          border-left: 3px solid var(--color-primary);
          line-height: 1.4;
        }
        .table-scroll { overflow-x: auto; }
        .table-compact th, .table-compact td { padding: 8px 12px; font-size: 13px; }
        .font-mono { font-family: monospace; }
        .text-success { color: var(--color-success); }
        .text-error { color: var(--color-error); }
      `}</style>
    </div>
  );
}

// Exchange transaction table (buy/sell) with dynamic currencies
interface ExchangeTransactionTableProps {
  title: string;
  subtitle?: string;
  transactions: DailyActivityReportType['transactions']['buy'];
  colorScheme: 'buy' | 'sell';
}

function ExchangeTransactionTable({ title, subtitle, transactions, colorScheme }: ExchangeTransactionTableProps) {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();

  const colorMap = {
    buy: 'var(--color-success)',
    sell: 'var(--color-primary)',
  };

  const headerColor = colorMap[colorScheme];

  // Calculate totals grouped by currency + weighted average rate (stablecoin/EUR pairs only)
  const totals = useMemo(() => {
    const inByCurrency: Record<string, number> = {};
    const outByCurrency: Record<string, number> = {};
    let profitEurSum = 0;
    let hasProfit = false;
    let eurPairSum = 0;
    let stablePairSum = 0;
    for (const tx of transactions) {
      inByCurrency[tx.currencyIn] = (inByCurrency[tx.currencyIn] || 0) + (parseFloat(tx.amountIn) || 0);
      outByCurrency[tx.currencyOut] = (outByCurrency[tx.currencyOut] || 0) + (parseFloat(tx.amountOut) || 0);
      if (tx.profitEur !== null && tx.profitEur !== undefined) {
        const p = parseFloat(tx.profitEur);
        if (Number.isFinite(p)) {
          profitEurSum += p;
          hasProfit = true;
        }
      }
      if (calculateDisplayRate(tx) !== null) {
        const aIn = parseFloat(tx.amountIn) || 0;
        const aOut = parseFloat(tx.amountOut) || 0;
        if (tx.currencyIn.toUpperCase() === 'EUR') {
          eurPairSum += aIn;
          stablePairSum += aOut;
        } else {
          stablePairSum += aIn;
          eurPairSum += aOut;
        }
      }
    }
    const avgRate = eurPairSum > 0 ? stablePairSum / eurPairSum : null;
    return { inByCurrency, outByCurrency, profitEurSum, hasProfit, avgRate };
  }, [transactions]);

  const formatTotals = (byCurrency: Record<string, number>) =>
    Object.entries(byCurrency)
      .map(([currency, amount]) => `${formatAmount(amount.toFixed(2), currency)} ${currency}`)
      .join(' / ');

  const profitClass = (value: number) =>
    value > 0 ? 'text-success' : value < 0 ? 'text-error' : '';

  return (
    <div className="card transaction-table-card">
      <h3 className="card-title transaction-title" style={{ borderLeftColor: headerColor }}>
        {title}
        <span className="transaction-count">({transactions.length})</span>
      </h3>
      {subtitle && <div className="transaction-subtitle">{subtitle}</div>}

      {transactions.length === 0 ? (
        <div className="empty-transactions">{t('dailyActivity.tables.noTransactions')}</div>
      ) : (
        <div className="table-scroll">
          <table className="table table-compact">
            <thead>
              <tr>
                <th>{t('dailyActivity.tables.client')}</th>
                <th className="text-right">{t('dailyActivity.tables.amountIn')}</th>
                <th className="text-right">{t('dailyActivity.tables.rate')}</th>
                <th className="text-right">{t('dailyActivity.tables.amountOut')}</th>
                <th className="text-right">{t('dailyActivity.tables.profit')}</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => {
                const profitEur = tx.profitEur !== null && tx.profitEur !== undefined
                  ? parseFloat(tx.profitEur)
                  : null;
                return (
                  <tr key={tx.id} className="tx-row" onClick={() => navigate(`/transactions/${tx.id}`)}>
                    <td className="name-cell">{tx.clientName || '-'}</td>
                    <td className="text-right font-mono">
                      {formatAmount(tx.amountIn, tx.currencyIn)} <span className="currency-label">{tx.currencyIn}</span>
                    </td>
                    <td className="text-right font-mono">{formatDisplayRate(calculateDisplayRate(tx), 5)}</td>
                    <td className="text-right font-mono">
                      {formatAmount(tx.amountOut, tx.currencyOut)} <span className="currency-label">{tx.currencyOut}</span>
                    </td>
                    <td className={`text-right font-mono ${profitEur !== null ? profitClass(profitEur) : ''}`}>
                      {profitEur !== null
                        ? <>{formatAmount(profitEur.toFixed(2), 'EUR')} <span className="currency-label">EUR</span></>
                        : '–'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td><strong>{t('dailyActivity.tables.total')}</strong></td>
                <td className="text-right font-mono"><strong>{formatTotals(totals.inByCurrency)}</strong></td>
                <td className="text-right font-mono"><strong>{formatDisplayRate(totals.avgRate, 5)}</strong></td>
                <td className="text-right font-mono"><strong>{formatTotals(totals.outByCurrency)}</strong></td>
                <td className={`text-right font-mono ${totals.hasProfit ? profitClass(totals.profitEurSum) : ''}`}>
                  {totals.hasProfit
                    ? <strong>{formatAmount(totals.profitEurSum.toFixed(2), 'EUR')} <span className="currency-label">EUR</span></strong>
                    : '–'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <style>{`
        .transaction-table-card {
          display: flex;
          flex-direction: column;
        }
        .transaction-title {
          border-left: 4px solid;
          padding-left: 12px;
          margin-left: -16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .transaction-count {
          font-size: 14px;
          font-weight: 400;
          color: var(--color-text-muted);
        }
        .transaction-subtitle {
          font-size: 11px;
          color: var(--color-text-muted);
          margin-top: -4px;
          margin-bottom: 4px;
          font-family: monospace;
        }
        .empty-transactions {
          padding: 24px;
          text-align: center;
          color: var(--color-text-muted);
        }
        .currency-label {
          font-size: 11px;
          color: var(--color-text-muted);
          font-weight: 400;
        }
        .table-scroll { overflow-x: auto; }
        .table-compact th, .table-compact td { padding: 8px 12px; font-size: 13px; }
        .font-mono { font-family: monospace; }
        .name-cell {
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .totals-row {
          background: var(--color-surface-hover);
        }
        .totals-row td {
          border-top: 2px solid var(--color-border);
        }
        .tx-row {
          cursor: pointer;
        }
        .tx-row:hover {
          background: var(--color-surface-hover);
        }
      `}</style>
    </div>
  );
}

// Expense transaction table with EUR equivalent column
interface ExpenseTransactionTableProps {
  title: string;
  transactions: Array<{ id: string; name: string; eur: string; rate: string; usdt: string }>;
  eurPurchaseRate: number | null;
  eurPurchaseRateSource: 'today' | 'previous' | null;
  eurPurchaseRateDate: string | null;
  isRange: boolean;
}

function ExpenseTransactionTable({ title, transactions, eurPurchaseRate, eurPurchaseRateSource, eurPurchaseRateDate, isRange }: ExpenseTransactionTableProps) {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();

  // For each expense: compute EUR equivalent
  // EUR expenses → amount as-is. USDT expenses → amount / eurPurchaseRate
  const rows = transactions.map(tx => {
    const eurAmount = parseFloat(tx.eur) || 0;
    const usdtAmount = parseFloat(tx.usdt) || 0;
    const isUsdt = usdtAmount > 0;
    const originalAmount = isUsdt ? usdtAmount : eurAmount;
    const originalCurrency = isUsdt ? 'USDT' : 'EUR';

    let eurEquivalent: number | null;
    if (isUsdt) {
      eurEquivalent = eurPurchaseRate !== null && eurPurchaseRate > 0
        ? usdtAmount / eurPurchaseRate
        : null;
    } else {
      eurEquivalent = eurAmount;
    }

    return { ...tx, originalAmount, originalCurrency, eurEquivalent, isUsdt };
  });

  // Totals
  let totalOriginalEur = 0;
  let totalOriginalUsdt = 0;
  let totalEurEquivalent: number | null = 0;
  for (const row of rows) {
    if (row.isUsdt) {
      totalOriginalUsdt += row.originalAmount;
    } else {
      totalOriginalEur += row.originalAmount;
    }
    if (row.eurEquivalent !== null && totalEurEquivalent !== null) {
      totalEurEquivalent += row.eurEquivalent;
    } else {
      totalEurEquivalent = null;
    }
  }

  return (
    <div className="card transaction-table-card">
      <h3 className="card-title transaction-title" style={{ borderLeftColor: 'var(--color-warning)' }}>
        {title}
        <span className="transaction-count">({transactions.length})</span>
      </h3>

      {transactions.length === 0 ? (
        <div className="empty-transactions">{t('dailyActivity.tables.noTransactions')}</div>
      ) : (
        <div className="table-scroll">
          <table className="table table-compact">
            <thead>
              <tr>
                <th>{t('dailyActivity.tables.name')}</th>
                <th className="text-right">{t('dailyActivity.tables.expenseAmount')}</th>
                <th className="text-right">{t('dailyActivity.tables.inEur')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="tx-row" onClick={() => navigate(`/transactions/${row.id}`)}>
                  <td className="name-cell">{row.name || '-'}</td>
                  <td className="text-right font-mono">
                    {formatAmount(row.originalAmount.toFixed(2), row.originalCurrency)}{' '}
                    <span className="currency-label">{row.originalCurrency}</span>
                  </td>
                  <td className="text-right font-mono">
                    {row.eurEquivalent !== null ? (
                      <>
                        {formatAmount(row.eurEquivalent.toFixed(2), 'EUR')}{' '}
                        <span className="currency-label">EUR</span>
                      </>
                    ) : (
                      <span className="no-rate-text" title={t('dailyActivity.tables.noEurRate')}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td><strong>{t('dailyActivity.tables.total')}</strong></td>
                <td className="text-right font-mono">
                  <strong>
                    {totalOriginalEur > 0 && <>{formatAmount(totalOriginalEur.toFixed(2), 'EUR')} EUR</>}
                    {totalOriginalEur > 0 && totalOriginalUsdt > 0 && ' / '}
                    {totalOriginalUsdt > 0 && <>{formatAmount(totalOriginalUsdt.toFixed(2), 'USDT')} USDT</>}
                  </strong>
                </td>
                <td className="text-right font-mono">
                  <strong>
                    {totalEurEquivalent !== null
                      ? <>{formatAmount(totalEurEquivalent.toFixed(2), 'EUR')} EUR</>
                      : <span className="no-rate-text">—</span>
                    }
                  </strong>
                </td>
              </tr>
            </tfoot>
          </table>
          {eurPurchaseRate === null && totalOriginalUsdt > 0 && (
            <div className="no-rate-warning">
              ⚠ {t('dailyActivity.tables.noEurRate')}
            </div>
          )}
          {eurPurchaseRate !== null && totalOriginalUsdt > 0 && (
            <div className={`rate-info${eurPurchaseRateSource === 'previous' ? ' rate-info-warning' : ''}`}>
              {eurPurchaseRateSource === 'previous' && eurPurchaseRateDate
                ? t('dailyActivity.tables.rateUsedPrevDay', { rate: eurPurchaseRate.toFixed(5), date: eurPurchaseRateDate })
                : isRange
                  ? t('dailyActivity.tables.rateUsedWeightedAvg', { rate: eurPurchaseRate.toFixed(5) })
                  : t('dailyActivity.tables.rateUsed', { rate: eurPurchaseRate.toFixed(5) })
              }
            </div>
          )}
        </div>
      )}

      <style>{`
        .transaction-table-card {
          display: flex;
          flex-direction: column;
        }
        .transaction-title {
          border-left: 4px solid;
          padding-left: 12px;
          margin-left: -16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .transaction-count {
          font-size: 14px;
          font-weight: 400;
          color: var(--color-text-muted);
        }
        .empty-transactions {
          padding: 24px;
          text-align: center;
          color: var(--color-text-muted);
        }
        .currency-label {
          font-size: 11px;
          color: var(--color-text-muted);
          font-weight: 400;
        }
        .no-rate-text {
          color: var(--color-warning);
        }
        .no-rate-warning {
          padding: 8px 12px;
          font-size: 11px;
          color: var(--color-warning);
          background: var(--color-surface-hover);
          border-radius: var(--radius);
          margin-top: 8px;
        }
        .rate-info {
          padding: 6px 12px;
          font-size: 10px;
          color: var(--color-text-muted);
          margin-top: 6px;
        }
        .rate-info-warning {
          color: var(--color-warning);
          background: color-mix(in srgb, var(--color-warning) 10%, transparent);
          border-radius: var(--radius);
        }
        .table-scroll { overflow-x: auto; }
        .table-compact th, .table-compact td { padding: 8px 12px; font-size: 13px; }
        .font-mono { font-family: monospace; }
        .name-cell {
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .totals-row {
          background: var(--color-surface-hover);
        }
        .totals-row td {
          border-top: 2px solid var(--color-border);
        }
        .tx-row {
          cursor: pointer;
        }
        .tx-row:hover {
          background: var(--color-surface-hover);
        }
      `}</style>
    </div>
  );
}

// Salary expense table — employee / amount / currency / date / in EUR
interface SalaryExpenseTableProps {
  title: string;
  transactions: Array<{
    id: string;
    employeeName: string | null;
    currency: string;
    eur: string;
    usdt: string;
    rub?: string;
    transactionDate: string | null;
  }>;
  eurPurchaseRate: number | null;
  eurPurchaseRateSource: 'today' | 'previous' | null;
  eurPurchaseRateDate: string | null;
  isRange: boolean;
}

function SalaryExpenseTable({ title, transactions, eurPurchaseRate, eurPurchaseRateSource, eurPurchaseRateDate, isRange }: SalaryExpenseTableProps) {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();

  const rows = transactions.map(tx => {
    const eurAmount = parseFloat(tx.eur) || 0;
    const usdtAmount = parseFloat(tx.usdt) || 0;
    const isUsdt = usdtAmount > 0;
    const originalAmount = isUsdt ? usdtAmount : eurAmount;
    const originalCurrency = isUsdt ? tx.currency : (tx.currency || 'EUR');

    let eurEquivalent: number | null;
    if (isUsdt) {
      eurEquivalent = eurPurchaseRate !== null && eurPurchaseRate > 0
        ? usdtAmount / eurPurchaseRate
        : null;
    } else {
      eurEquivalent = eurAmount;
    }

    return { ...tx, originalAmount, originalCurrency, eurEquivalent, isUsdt };
  });

  // Totals per-currency (for the summary row) and EUR equivalent
  let totalOriginalEur = 0;
  let totalOriginalUsdt = 0;
  let totalEurEquivalent: number | null = 0;
  for (const row of rows) {
    if (row.isUsdt) {
      totalOriginalUsdt += row.originalAmount;
    } else {
      totalOriginalEur += row.originalAmount;
    }
    if (row.eurEquivalent !== null && totalEurEquivalent !== null) {
      totalEurEquivalent += row.eurEquivalent;
    } else {
      totalEurEquivalent = null;
    }
  }

  const formatSalaryDate = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="card transaction-table-card">
      <h3 className="card-title transaction-title" style={{ borderLeftColor: 'var(--color-warning)' }}>
        {title}
        <span className="transaction-count">({transactions.length})</span>
      </h3>

      {transactions.length === 0 ? (
        <div className="empty-transactions">{t('dailyActivity.tables.noTransactions')}</div>
      ) : (
        <div className="table-scroll">
          <table className="table table-compact">
            <thead>
              <tr>
                <th>{t('dailyActivity.tables.employee')}</th>
                <th className="text-right">{t('dailyActivity.tables.expenseAmount')}</th>
                <th>{t('dailyActivity.tables.currency')}</th>
                <th>{t('dailyActivity.tables.date')}</th>
                <th className="text-right">{t('dailyActivity.tables.inEur')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="tx-row" onClick={() => navigate(`/transactions/${row.id}`)}>
                  <td className="name-cell">{row.employeeName || '—'}</td>
                  <td className="text-right font-mono">
                    {formatAmount(row.originalAmount.toFixed(2), row.originalCurrency)}
                  </td>
                  <td><span className="currency-label">{row.originalCurrency}</span></td>
                  <td>{formatSalaryDate(row.transactionDate)}</td>
                  <td className="text-right font-mono">
                    {row.eurEquivalent !== null ? (
                      <>
                        {formatAmount(row.eurEquivalent.toFixed(2), 'EUR')}{' '}
                        <span className="currency-label">EUR</span>
                      </>
                    ) : (
                      <span className="no-rate-text" title={t('dailyActivity.tables.noEurRate')}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td><strong>{t('dailyActivity.tables.total')}</strong></td>
                <td className="text-right font-mono">
                  <strong>
                    {totalOriginalEur > 0 && <>{formatAmount(totalOriginalEur.toFixed(2), 'EUR')} EUR</>}
                    {totalOriginalEur > 0 && totalOriginalUsdt > 0 && ' / '}
                    {totalOriginalUsdt > 0 && <>{formatAmount(totalOriginalUsdt.toFixed(2), 'USDT')} USDT</>}
                  </strong>
                </td>
                <td />
                <td />
                <td className="text-right font-mono">
                  <strong>
                    {totalEurEquivalent !== null
                      ? <>{formatAmount(totalEurEquivalent.toFixed(2), 'EUR')} EUR</>
                      : <span className="no-rate-text">—</span>
                    }
                  </strong>
                </td>
              </tr>
            </tfoot>
          </table>
          {eurPurchaseRate === null && totalOriginalUsdt > 0 && (
            <div className="no-rate-warning">
              ⚠ {t('dailyActivity.tables.noEurRate')}
            </div>
          )}
          {eurPurchaseRate !== null && totalOriginalUsdt > 0 && (
            <div className={`rate-info${eurPurchaseRateSource === 'previous' ? ' rate-info-warning' : ''}`}>
              {eurPurchaseRateSource === 'previous' && eurPurchaseRateDate
                ? t('dailyActivity.tables.rateUsedPrevDay', { rate: eurPurchaseRate.toFixed(5), date: eurPurchaseRateDate })
                : isRange
                  ? t('dailyActivity.tables.rateUsedWeightedAvg', { rate: eurPurchaseRate.toFixed(5) })
                  : t('dailyActivity.tables.rateUsed', { rate: eurPurchaseRate.toFixed(5) })
              }
            </div>
          )}
        </div>
      )}
      <style>{`
        .tx-row {
          cursor: pointer;
        }
        .tx-row:hover {
          background: var(--color-surface-hover);
        }
      `}</style>
    </div>
  );
}

// Legacy transaction table (debt) with EUR/USDT fixed columns
interface LegacyTransactionTableProps {
  title: string;
  transactions: Array<{ id: string; clientName?: string; name?: string; eur: string; rate: string; usdt: string }>;
  colorScheme: 'debt';
  nameField: 'clientName' | 'name';
}

function LegacyTransactionTable({ title, transactions, colorScheme, nameField }: LegacyTransactionTableProps) {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();

  const headerColor = colorScheme === 'debt' ? 'var(--color-error)' : 'var(--color-warning)';

  // Calculate totals
  let totalEur = 0;
  let totalUsdt = 0;
  for (const tx of transactions) {
    totalEur += parseFloat(tx.eur) || 0;
    totalUsdt += parseFloat(tx.usdt) || 0;
  }

  return (
    <div className="card transaction-table-card">
      <h3 className="card-title transaction-title" style={{ borderLeftColor: headerColor }}>
        {title}
        <span className="transaction-count">({transactions.length})</span>
      </h3>

      {transactions.length === 0 ? (
        <div className="empty-transactions">{t('dailyActivity.tables.noTransactions')}</div>
      ) : (
        <div className="table-scroll">
          <table className="table table-compact">
            <thead>
              <tr>
                <th>{nameField === 'clientName' ? t('dailyActivity.tables.client') : t('dailyActivity.tables.name')}</th>
                <th className="text-right">EUR</th>
                <th className="text-right">{t('dailyActivity.tables.rate')}</th>
                <th className="text-right">USDT / USDC</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id} className="tx-row" onClick={() => navigate(`/transactions/${tx.id}`)}>
                  <td className="name-cell">{(tx as any)[nameField] || '-'}</td>
                  <td className="text-right font-mono">{formatAmount(tx.eur)}</td>
                  <td className="text-right font-mono">{(() => {
                    const eur = parseFloat(tx.eur);
                    const usdt = parseFloat(tx.usdt);
                    return eur && usdt ? (usdt / eur).toFixed(5) : '–';
                  })()}</td>
                  <td className="text-right font-mono">{formatAmount(tx.usdt)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td><strong>{t('dailyActivity.tables.total')}</strong></td>
                <td className="text-right font-mono"><strong>{formatAmount(totalEur.toFixed(2))}</strong></td>
                <td></td>
                <td className="text-right font-mono"><strong>{formatAmount(totalUsdt.toFixed(2))}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <style>{`
        .transaction-table-card {
          display: flex;
          flex-direction: column;
        }
        .transaction-title {
          border-left: 4px solid;
          padding-left: 12px;
          margin-left: -16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .transaction-count {
          font-size: 14px;
          font-weight: 400;
          color: var(--color-text-muted);
        }
        .empty-transactions {
          padding: 24px;
          text-align: center;
          color: var(--color-text-muted);
        }
        .table-scroll { overflow-x: auto; }
        .table-compact th, .table-compact td { padding: 8px 12px; font-size: 13px; }
        .font-mono { font-family: monospace; }
        .name-cell {
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .totals-row {
          background: var(--color-surface-hover);
        }
        .totals-row td {
          border-top: 2px solid var(--color-border);
        }
        .tx-row {
          cursor: pointer;
        }
        .tx-row:hover {
          background: var(--color-surface-hover);
        }
      `}</style>
    </div>
  );
}
